import type { ComponentType, FC, ReactNode } from 'react'

import {
    Banknote,
    Bell,
    CalendarDays,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    Circle,
    CircleCheck,
    FileText,
    House,
    KeyRound,
    Landmark,
    LayoutGrid,
    MessageSquare,
    Search,
    Settings,
    Users
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/generals/logo'

export type DealPhase = 'reading' | 'building' | 'done'

type IconComponent = ComponentType<{ className?: string }>

type Tone = 'leaf' | 'honey' | 'mist'

interface TimelineRow {
    icon: IconComponent
    title: string
    date: string
    detail: string
    status?: {
        label: string
        tone: Tone
    }
    approval?: boolean
}

interface DealMockupProps {
    phase: DealPhase
    visibleRows: number
}

interface StatusPillProps {
    label: string
    tone: Tone
}

export const TIMELINE_ROWS: Array<TimelineRow> = [
    {
        icon: Banknote,
        title: 'Earnest money',
        date: 'Oct 12',
        detail: '$12,000 wired to escrow, receipt filed to the deal',
        status: { label: 'Received', tone: 'leaf' }
    },
    {
        icon: Search,
        title: 'Inspection period ends',
        date: 'Oct 19',
        detail: 'Inspector booked Oct 15, 10:00 am. Remind the buyers the day before?',
        approval: true
    },
    {
        icon: House,
        title: 'Appraisal deadline',
        date: 'Oct 28',
        detail: 'Ordered by the lender, appraiser assigned',
        status: { label: 'Ordered', tone: 'honey' }
    },
    {
        icon: Landmark,
        title: 'Financing contingency',
        date: 'Nov 3',
        detail: 'Loan in underwriting, conditions shared with the buyers',
        status: { label: 'Upcoming', tone: 'mist' }
    },
    {
        icon: KeyRound,
        title: 'Closing day',
        date: 'Nov 14',
        detail: 'Title office at 2:00 pm, final walkthrough at 11:00 am',
        status: { label: 'Scheduled', tone: 'mist' }
    }
]

const RAIL_ICONS: Array<{ icon: IconComponent, active?: boolean }> = [
    { icon: LayoutGrid, active: true },
    { icon: CalendarDays },
    { icon: FileText },
    { icon: MessageSquare },
    { icon: Users }
]

const SUB_STEPS: Array<{ label: string, state: 'done' | 'current' | 'todo' }> = [
    { label: 'Contract & addenda', state: 'done' },
    { label: 'Key dates', state: 'current' },
    { label: 'Disclosures', state: 'todo' },
    { label: 'Introductions', state: 'todo' },
    { label: 'Follow-ups', state: 'todo' }
]

const LATER_STEPS = ['Inspection', 'Appraisal', 'Financing', 'Closing']

const SIDE_LINKS: Array<{ icon: IconComponent, label: string }> = [
    { icon: Users, label: 'Contacts' },
    { icon: FileText, label: 'Documents' },
    { icon: MessageSquare, label: 'Messages' },
    { icon: Settings, label: 'Settings' }
]

const MOBILE_STEPS = [
    'Contract read, 32 pages',
    'Timeline built, 11 dates',
    'Inspector booked for Oct 15',
    'Lender and title updated'
]

const TONES: Record<Tone, string> = {
    leaf: 'bg-[#e9eee2] text-leaf',
    honey: 'bg-[#f6eedd] text-honey',
    mist: 'bg-[#f1efe8] text-mist'
}

const STATUS_COPY: Record<DealPhase, string> = {
    reading: 'Reading 32 pages, two counteroffers and one addendum.',
    building: 'Working out every deadline and lining up the people involved.',
    done: 'Closewell read the executed contract and built your timeline. Nothing needs you right now, and you will hear the moment something does.'
}

const StatusPill: FC<StatusPillProps> = ({ label, tone }): ReactNode => {
    return (
        <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium', TONES[tone])}>
            <span className='size-1.5 rounded-full bg-current' />

            {label}
        </span>
    )
}

const PhaseChip: FC<{ phase: DealPhase }> = ({ phase }): ReactNode => {
    if (phase === 'done') return <StatusPill label='On track' tone='leaf' />

    return (
        <span className='inline-flex items-center gap-1 rounded-full bg-[#f6eedd] px-2 py-0.5 text-[10px] font-medium text-honey'>
            <span className='soft-pulse size-1.5 rounded-full bg-current' />

            {phase === 'reading' ? 'Reading contract' : 'Building timeline'}
        </span>
    )
}

const DesktopWindow: FC<DealMockupProps> = ({ phase, visibleRows }): ReactNode => {
    return (
        <div className='hidden rounded-[20px] border border-white/60 bg-white/30 p-2 shadow-[0_40px_90px_-32px_rgba(38,56,92,0.5)] backdrop-blur-md md:block'>
            <div className='relative flex h-7 items-center px-2'>
                <div className='flex gap-1.5'>
                    <span className='size-2.5 rounded-full bg-white/90' />
                    <span className='size-2.5 rounded-full bg-white/60' />
                    <span className='size-2.5 rounded-full bg-white/45' />
                </div>

                <span className='absolute left-1/2 -translate-x-1/2 rounded-full bg-white/45 px-3 py-0.5 text-[10.5px] text-foreground/50'>
                    Closewell · Deal room
                </span>
            </div>

            <div className='flex h-[560px] overflow-hidden rounded-[14px] bg-paper text-left'>
                <div className='flex w-12 shrink-0 flex-col items-center gap-1 border-r border-[#ebe8df] py-3'>
                    <LogoMark className='mb-3 size-5 text-foreground' />

                    {RAIL_ICONS.map(({ icon: Icon, active }, index) => (
                        <span
                            key={index}
                            className={cn('flex size-8 items-center justify-center rounded-md text-mist', active && 'bg-[#eeebe3] text-foreground')}
                        >
                            <Icon className='size-4' />
                        </span>
                    ))}

                    <span className='mt-auto flex size-7 items-center justify-center rounded-full bg-primary text-[9.5px] font-medium text-primary-foreground'>
                        MC
                    </span>
                </div>

                <div className='flex min-w-0 flex-1 flex-col'>
                    <div className='flex items-center justify-between gap-4 border-b border-[#ebe8df] px-5 py-3'>
                        <div className='flex min-w-0 items-center gap-3'>
                            <span className='flex shrink-0 items-center gap-0.5 text-[9.5px] font-medium tracking-[0.14em] text-mist uppercase'>
                                <ChevronLeft className='size-3' />
                                Deals
                            </span>

                            <div className='min-w-0'>
                                <p className='truncate font-heading text-[19px] leading-tight text-foreground'>
                                    214 Wren Hollow Lane, Asheville, NC 28803
                                </p>

                                <p className='text-[10.5px] text-mist'>
                                    Closing Nov 14, 2026 · Buyer side
                                </p>
                            </div>
                        </div>

                        <div className='flex shrink-0 items-center gap-3'>
                            <Bell className='size-3.5 text-mist' />

                            <span className='flex items-center gap-1.5 rounded-full border border-[#e6e3da] py-0.5 pr-2.5 pl-0.5 text-[10.5px] font-medium text-foreground/80'>
                                <span className='flex size-5 items-center justify-center rounded-full bg-[#dcd6c5] text-[8.5px]'>
                                    SP
                                </span>

                                Sam & Jordan
                                <ChevronDown className='size-3 text-mist' />
                            </span>

                            <div className='hidden text-right leading-tight lg:block'>
                                <p className='text-[10.5px] font-medium text-foreground'>
                                    Maya Chen
                                </p>

                                <p className='text-[9.5px] text-mist'>
                                    Buyer&apos;s agent
                                </p>
                            </div>

                            <span className='flex size-6 items-center justify-center rounded-full bg-primary text-[9px] font-medium text-primary-foreground'>
                                MC
                            </span>
                        </div>
                    </div>

                    <div className='flex min-h-0 flex-1'>
                        <div className='hidden w-[208px] shrink-0 flex-col border-r border-[#ebe8df] px-3 py-4 lg:flex'>
                            <div className='flex items-center justify-between rounded-md px-2 py-1.5'>
                                <span className='flex items-center gap-2 text-[11.5px] font-semibold text-foreground'>
                                    <span className='flex size-4 items-center justify-center rounded-full border border-foreground/70'>
                                        <span className='size-2 rounded-full bg-foreground/70' />
                                    </span>

                                    Under contract
                                </span>

                                <ChevronUp className='size-3 text-mist' />
                            </div>

                            <ul className='mt-1 space-y-0.5 pl-4'>
                                {SUB_STEPS.map(step => (
                                    <li
                                        key={step.label}
                                        className={cn(
                                            'flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px]',
                                            step.state === 'current' ? 'bg-[#eeebe3] font-medium text-foreground' : 'text-mist'
                                        )}
                                    >
                                        {step.state === 'done' ? <CircleCheck className='size-3.5 text-leaf' /> : <Circle className='size-3.5' />}
                                        {step.label}
                                    </li>
                                ))}
                            </ul>

                            <ul className='mt-2 space-y-0.5'>
                                {LATER_STEPS.map(step => (
                                    <li key={step} className='flex items-center justify-between rounded-md px-2 py-1.5 text-[11.5px] text-foreground/70'>
                                        <span className='flex items-center gap-2'>
                                            <Circle className='size-4 text-pebble' />
                                            {step}
                                        </span>

                                        <ChevronDown className='size-3 text-pebble' />
                                    </li>
                                ))}
                            </ul>

                            <ul className='mt-auto space-y-0.5'>
                                {SIDE_LINKS.map(({ icon: Icon, label }) => (
                                    <li key={label} className='flex items-center gap-2 px-2 py-1 text-[10.5px] text-mist'>
                                        <Icon className='size-3.5' />
                                        {label}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className='relative flex min-w-0 flex-1 flex-col px-8 pt-6 pb-4'>
                            <div className='flex items-center gap-2'>
                                <p className='text-[14px] font-semibold text-foreground'>
                                    Key dates
                                </p>

                                <PhaseChip phase={phase} />
                            </div>

                            <p className='mt-1.5 max-w-[27rem] text-[11.5px] leading-relaxed text-muted-foreground'>
                                {STATUS_COPY[phase]}
                            </p>

                            <div className={cn('mt-3 h-[3px] w-56 overflow-hidden rounded-full bg-[#ebe8df] transition-opacity duration-300', phase === 'done' && 'opacity-0')}>
                                <div className='shimmer-line h-full w-1/2 rounded-full bg-primary/60' />
                            </div>

                            <ul className='mt-3 space-y-2'>
                                {TIMELINE_ROWS.map(({ icon: Icon, title, date, detail, status, approval }, index) => (
                                    <li
                                        key={title}
                                        className={cn(
                                            'flex items-center gap-3 rounded-lg border border-[#e9e6dd] bg-white px-3 py-2.5 transition-[opacity,translate] duration-500 ease-out',
                                            index < visibleRows ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
                                        )}
                                    >
                                        <span className='flex size-7 shrink-0 items-center justify-center rounded-md bg-[#f3f1ea] text-foreground/70'>
                                            <Icon className='size-3.5' />
                                        </span>

                                        <div className='min-w-0 flex-1'>
                                            <p className='text-[11.5px] font-semibold text-foreground'>
                                                {title}
                                                <span className='font-normal text-mist'> · {date}</span>
                                            </p>

                                            <p className='truncate text-[10.5px] text-mist'>
                                                {detail}
                                            </p>
                                        </div>

                                        {approval ? (
                                            <span className='flex shrink-0 gap-1.5'>
                                                <span className='rounded-md bg-primary px-2.5 py-1 text-[10px] font-medium text-primary-foreground'>
                                                    Approve
                                                </span>

                                                <span className='rounded-md border border-[#e3e0d6] px-2.5 py-1 text-[10px] font-medium text-foreground/80'>
                                                    Edit message
                                                </span>
                                            </span>
                                        ) : status ? (
                                            <StatusPill label={status.label} tone={status.tone} />
                                        ) : null}
                                    </li>
                                ))}
                            </ul>

                            <div className='mt-2.5 flex h-8 items-center justify-center rounded-lg border border-dashed border-[#d9d5c8] text-[11px] font-medium text-foreground/70'>
                                + Add a date
                            </div>

                            <p className='mt-3 text-[10.5px] text-mist'>
                                Every date syncs to calendars and the client portal →
                            </p>

                            <div className='mt-auto flex items-center justify-between border-t border-[#ebe8df] pt-3 text-[10.5px] text-mist'>
                                <span>Back</span>
                                <span>Continue to disclosures →</span>
                            </div>

                            <span className='absolute right-5 bottom-14 flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_8px_20px_-6px_rgba(35,34,30,0.45)]'>
                                <LogoMark className='size-[18px]' />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

const MobileCard: FC<DealMockupProps> = ({ phase, visibleRows }): ReactNode => {
    const completed = phase === 'reading' ? 0 : Math.min(MOBILE_STEPS.length, visibleRows)

    return (
        <div className='rounded-2xl border border-white/70 bg-paper p-5 text-left shadow-[0_30px_60px_-30px_rgba(38,56,92,0.5)] md:hidden'>
            <p className='font-heading text-[25px] leading-[1.08] text-foreground'>
                214 Wren Hollow Lane, Asheville, NC
            </p>

            <p className='mt-1.5 text-xs text-mist'>
                Closing Nov 14, 2026
            </p>

            <ul className='mt-4 space-y-2'>
                {MOBILE_STEPS.map((label, index) => (
                    <li key={label} className='flex items-center gap-2.5 rounded-lg border border-[#e9e6dd] bg-white px-3 py-2.5 text-[13px] text-foreground'>
                        {index < completed ? <CircleCheck className='size-4 shrink-0 text-leaf' /> : <Circle className='size-4 shrink-0 text-pebble' />}
                        {label}
                    </li>
                ))}
            </ul>

            <p className='mt-3 flex items-center gap-1.5 text-xs text-mist'>
                <LogoMark className={cn('size-3.5', phase !== 'done' && 'soft-pulse')} />
                {phase === 'done' ? 'Timeline ready. Nothing needs you right now.' : 'Closewell is reading the contract'}
            </p>
        </div>
    )
}

export const DealMockup: FC<DealMockupProps> = (props): ReactNode => {
    return (
        <div aria-hidden='true'>
            <DesktopWindow {...props} />
            <MobileCard {...props} />
        </div>
    )
}
